import { API_BASE_URL, apiGet, apiPost, apiPostFormRaw, getTenantId } from './client';
import type {
  SwapUfAccount,
  SwapUfCreateRequest,
  SwapUfCreateResult,
  SwapUfImportResult,
  SwapUfImportRow,
  SwapUfLeg,
  SwapUfLegDefaults,
} from '../types/SwapUf';

// The server refuses with 400 { error: "<Hebrew message>" }. apiPost wraps that as
// "POST <path> failed: <message>"; the screen shows the message as-is.
export function swapUfErrorMessage(e: unknown): string {
  const m = e instanceof Error ? e.message : String(e);
  const i = m.indexOf(' failed: ');
  return i >= 0 ? m.slice(i + ' failed: '.length) : m;
}

export function getSwapUfLegs(p: {
  acc?: string | null;
  fundId?: number | null;
  assetId?: number | null;
  includeSettled?: boolean;
}): Promise<SwapUfLeg[]> {
  const q = new URLSearchParams();
  if (p.acc) q.set('acc', String(p.acc));
  if (p.fundId) q.set('fundId', String(p.fundId));
  if (p.assetId) q.set('assetId', String(p.assetId));
  if (p.includeSettled) q.set('includeSettled', 'true');
  const s = q.toString();
  return apiGet<SwapUfLeg[]>(`/api/swap-uf/legs${s ? `?${s}` : ''}`);
}

export function getSwapUfAccounts(fundId?: number | null): Promise<SwapUfAccount[]> {
  return apiGet<SwapUfAccount[]>(`/api/swap-uf/accounts${fundId ? `?fundId=${fundId}` : ''}`);
}

// 204 (the account has no swap history) -> null. apiGet would choke on the empty body,
// so this one builds its own fetch and injects X-Tenant-Id the way the client does.
export async function getSwapUfLegDefaults(
  acc: string,
  dealDate: string,
): Promise<SwapUfLegDefaults | null> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const tenantId = getTenantId();
  if (tenantId !== null) headers['X-Tenant-Id'] = String(tenantId);
  const res = await fetch(
    `${API_BASE_URL}/api/swap-uf/leg-defaults?acc=${encodeURIComponent(acc)}&dealDate=${encodeURIComponent(dealDate)}`,
    { credentials: 'include', headers },
  );
  if (res.status === 204) return null;
  if (!res.ok) throw new Error(`GET leg-defaults failed: ${res.status}`);
  return (await res.json()) as SwapUfLegDefaults;
}

export function createSwapUfLeg(body: SwapUfCreateRequest): Promise<SwapUfCreateResult> {
  return apiPost<SwapUfCreateRequest, SwapUfCreateResult>('/api/swap-uf/legs', body);
}

export async function importSwapUfFile(file: File): Promise<SwapUfImportResult> {
  const form = new FormData();
  form.append('file', file);
  const res = await apiPostFormRaw('/api/swap-uf/import', form);
  if (!res.ok) {
    let detail = `${res.status}`;
    try {
      const data = await res.json();
      if (data && typeof data.error === 'string') detail = data.error;
    } catch {
      // not JSON
    }
    throw new Error(`POST /api/swap-uf/import failed: ${detail}`);
  }
  return (await res.json()) as SwapUfImportResult;
}

export function getSwapUfImportRows(p: {
  status: 'exception' | 'all';
  fromDate?: string;
  toDate?: string;
}): Promise<SwapUfImportRow[]> {
  const q = new URLSearchParams({ status: p.status });
  if (p.fromDate) q.set('fromDate', p.fromDate);
  if (p.toDate) q.set('toDate', p.toDate);
  return apiGet<SwapUfImportRow[]>(`/api/swap-uf/import-rows?${q.toString()}`);
}

export function resolveSwapUfImportRow(id: number, acc: string): Promise<SwapUfImportRow> {
  return apiPost<{ acc: string }, SwapUfImportRow>(`/api/swap-uf/import-rows/${id}/resolve`, { acc });
}

export function dismissSwapUfImportRow(id: number): Promise<void> {
  return apiPost<undefined, void>(`/api/swap-uf/import-rows/${id}/dismiss`);
}

// Sample xlsx: fetched as a blob (the link needs credentials + tenant header) and saved.
export async function downloadSwapUfSample(): Promise<void> {
  const headers: Record<string, string> = {};
  const tenantId = getTenantId();
  if (tenantId !== null) headers['X-Tenant-Id'] = String(tenantId);
  const res = await fetch(`${API_BASE_URL}/api/swap-uf/import-sample`, {
    credentials: 'include',
    headers,
  });
  if (!res.ok) throw new Error(`GET import-sample failed: ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'swap-uf-sample.xlsx';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
